/**
 * ApiAccessSection — Profil-Sektion "API-Zugang".
 *
 * Nur sichtbar wenn `sellerData.apiAccess === true` (strikt, kein truthy).
 * Ermöglicht das Generieren, Auflisten und Widerrufen von API-Keys.
 *
 * Klartext-Key: wird ausschließlich nach dem Generieren einmalig in lokalem
 * Component-State gehalten und angezeigt (Copy + Warnhinweis).
 * Kein localStorage/sessionStorage. Nach Page-Reload unwiederbringlich weg.
 *
 * Muster: EmailPreferencesSection in AccountPage.tsx.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { IconKey, IconCopy, IconCheck, IconDelete, IconRefresh, IconNew } from "../../icons";
import { ConfirmationDialog } from "../ConfirmationDialog";
import {
  generateApiKey,
  listApiKeys,
  revokeApiKey,
  rotateApiKey,
  ApiKeyListItem,
  GenerateApiKeyResponse,
  API_KEY_SCOPES,
} from "../../api/apiKeysApi";
import { Seller } from "../../types/invoiceTypes";
import logger from "../../utils/logger";
import { formatDate as formatDateCentral } from "../../utils/formatters";
import WebhookEndpointsPanel from "./WebhookEndpointsPanel";

/**
 * M12d-4a: Scope-Auswahl/-Anzeige + Rotate-Button — fail-closed hinter Flag.
 * Bei AUS (Default) ist ApiAccessSectionInner byte-identisch zum Vor-M12d-Stand:
 * kein Scope-Selector, keine Scope-Badges, kein Rotate-Button, `generateApiKey`
 * wird OHNE scopes aufgerufen.
 */
const API_V2_ENABLED = process.env.REACT_APP_API_V2_ENABLED === "true";

/** Default-Scope-Auswahl beim Generieren, wenn der Scope-Selector sichtbar ist. */
const DEFAULT_SELECTED_SCOPES: string[] = ["invoice:create"];

/** i18n-Key-Suffix je Scope-String (z.B. "invoice:create" -> "invoiceCreate"). */
const SCOPE_LABEL_KEYS: Record<string, string> = {
  "invoice:create": "invoiceCreate",
  "invoice:read": "invoiceRead",
  "invoice:cancel": "invoiceCancel",
  "webhook:manage": "webhookManage",
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ApiAccessSectionProps {
  sellerData: Seller | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ApiAccessSection: React.FC<ApiAccessSectionProps> = ({ sellerData }) => {
  const { t } = useTranslation("account");

  // Gate: nur bei explizitem apiAccess === true (nicht truthy).
  if (sellerData?.apiAccess !== true) {
    return null;
  }

  return <ApiAccessSectionInner />;
};

/**
 * Innerer Teil — wird nur gerendert wenn apiAccess === true.
 * Eigener Hooks-Scope, damit die Gate-Bedingung sauber bleibt.
 */
const ApiAccessSectionInner: React.FC = () => {
  const { t, i18n } = useTranslation("account");

  // --- State ---
  const [keys, setKeys] = useState<ApiKeyListItem[]>([]);
  const [isLoadingKeys, setIsLoadingKeys] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Generierung
  const [labelInput, setLabelInput] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  // Scope-Auswahl beim Generieren — nur relevant/gerendert wenn API_V2_ENABLED
  const [selectedScopes, setSelectedScopes] = useState<string[]>(DEFAULT_SELECTED_SCOPES);

  // Einmalig angezeigter Klartext-Key (nur in Component-State, nie persistiert) —
  // wird sowohl nach Generieren ALS AUCH nach Rotieren befüllt (identische Response-Shape).
  const [newKeyResult, setNewKeyResult] = useState<GenerateApiKeyResponse | null>(null);
  const [copied, setCopied] = useState<boolean>(false);

  // Widerruf-Bestätigungsdialog
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyListItem | null>(null);
  const [isRevoking, setIsRevoking] = useState<boolean>(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  // Rotation (nur bei API_V2_ENABLED erreichbar)
  const [rotateTarget, setRotateTarget] = useState<ApiKeyListItem | null>(null);
  const [isRotating, setIsRotating] = useState<boolean>(false);
  const [rotateError, setRotateError] = useState<string | null>(null);

  // --- Laden der Key-Liste ---
  const loadKeys = useCallback(async () => {
    setIsLoadingKeys(true);
    setLoadError(null);
    try {
      const fetched = await listApiKeys();
      setKeys(fetched);
    } catch (error: unknown) {
      logger.error("ApiAccessSection: listApiKeys failed", error);
      setLoadError(t("apiAccess.error"));
    } finally {
      setIsLoadingKeys(false);
    }
  }, [t]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  // --- Key generieren ---
  const handleGenerate = async () => {
    setIsGenerating(true);
    setGenerateError(null);
    setNewKeyResult(null);
    setCopied(false);
    try {
      // Flag AUS: generateApiKey wird OHNE scopes aufgerufen (byte-identisch zu vorher).
      const result = await generateApiKey(
        labelInput.trim() || undefined,
        API_V2_ENABLED ? selectedScopes : undefined
      );
      setNewKeyResult(result);
      setLabelInput("");
      setSelectedScopes(DEFAULT_SELECTED_SCOPES);
      // Key-Liste neu laden (neuer Key erscheint mit maskiertem Prefix)
      await loadKeys();
    } catch (error: unknown) {
      logger.error("ApiAccessSection: generateApiKey failed", error);
      setGenerateError(t("apiAccess.error"));
    } finally {
      setIsGenerating(false);
    }
  };

  // --- Scope-Checkbox togglen (nur bei sichtbarem Selector genutzt) ---
  const toggleScope = (scope: string) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  // --- Key in Clipboard kopieren ---
  const handleCopy = async () => {
    if (!newKeyResult?.plaintextKey) return;
    try {
      await navigator.clipboard.writeText(newKeyResult.plaintextKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (error: unknown) {
      logger.error("ApiAccessSection: clipboard write failed", error);
    }
  };

  // --- Widerruf auslösen (nach Bestätigung) ---
  const handleRevokeConfirmed = async () => {
    if (!revokeTarget) return;
    setIsRevoking(true);
    setRevokeError(null);
    try {
      await revokeApiKey(revokeTarget.keyId);
      setRevokeTarget(null);
      // Liste neu laden — widerrufener Key wird mit revoked=true angezeigt
      await loadKeys();
    } catch (error: unknown) {
      logger.error(
        `ApiAccessSection: revokeApiKey failed for ${revokeTarget.keyId}`,
        error
      );
      setRevokeError(t("apiAccess.error"));
      setRevokeTarget(null);
    } finally {
      setIsRevoking(false);
    }
  };

  // --- Rotation auslösen (nach Bestätigung) — nur erreichbar bei API_V2_ENABLED ---
  const handleRotateConfirmed = async () => {
    if (!rotateTarget || isRotating) return;
    setIsRotating(true);
    setRotateError(null);
    try {
      const result = await rotateApiKey(rotateTarget.keyId);
      setRotateTarget(null);
      // Neuen Klartext-Key im bestehenden einmaligen Anzeige-Block zeigen (newKeyResult-Muster)
      setNewKeyResult(result);
      setCopied(false);
      // Liste neu laden — alter Key zeigt jetzt die Grace-Period, neuer Key erscheint
      await loadKeys();
    } catch (error: unknown) {
      logger.error(
        `ApiAccessSection: rotateApiKey failed for ${rotateTarget.keyId}`,
        error
      );
      setRotateError(t("apiAccess.error"));
      setRotateTarget(null);
    } finally {
      setIsRotating(false);
    }
  };

  // --- Datum formatieren (ISO → lokale Darstellung, zentrales formatDate, M-G) ---
  const formatDate = (iso: string | null): string => {
    if (!iso) return t("apiAccess.neverUsed");
    return formatDateCentral(iso, { locale: i18n.language, includeTime: true });
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="mt-8 bg-surface-raised shadow-lg rounded-xl border border-hairline p-6">
      {/* Abschnitts-Header */}
      <h2 className="text-xl font-semibold mb-2 text-primary flex items-center">
        <IconKey size={20} className="mr-2 text-accent-ink" />
        {t("apiAccess.title")}
      </h2>
      <p className="text-muted text-sm mb-6">{t("apiAccess.description")}</p>

      {/* Neuen Key generieren */}
      <div className="mb-6 p-4 bg-[rgb(var(--surface-inset-rgb)/0.4)] rounded-lg border border-strong">
        <p className="text-secondary text-sm font-medium mb-3">
          {t("apiAccess.generate")}
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={labelInput}
            onChange={(e) => setLabelInput(e.target.value)}
            placeholder={t("apiAccess.labelPlaceholder")}
            maxLength={80}
            disabled={isGenerating}
            className="flex-1 px-3 py-2 bg-[rgb(var(--surface-base-rgb)/0.6)] border border-strong rounded-md text-primary text-sm placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
          />
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-[#0C1322] text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? (
              <>
                <IconRefresh className="animate-spin" />
                {t("apiAccess.generating")}
              </>
            ) : (
              <>
                <IconNew />
                {t("apiAccess.generate")}
              </>
            )}
          </button>
        </div>

        {/* Scope-Auswahl (nur bei REACT_APP_API_V2_ENABLED=true) */}
        {API_V2_ENABLED && (
          <div className="mt-4">
            <p className="text-secondary text-xs font-medium mb-2">
              {t("apiAccess.scopesLabel")}
            </p>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {API_KEY_SCOPES.map((scope) => (
                <label
                  key={scope}
                  className="flex items-center gap-2 text-sm text-secondary cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedScopes.includes(scope)}
                    onChange={() => toggleScope(scope)}
                    disabled={isGenerating}
                    className="rounded border-strong bg-[rgb(var(--surface-base-rgb)/0.6)] text-indigo-500 focus:ring-indigo-500 disabled:opacity-50"
                  />
                  {t(`apiAccess.scope.${SCOPE_LABEL_KEYS[scope]}`)}
                </label>
              ))}
            </div>
          </div>
        )}

        {generateError && (
          <p className="mt-3 text-sm text-[var(--tone-danger-fg)]">{generateError}</p>
        )}
      </div>

      {/* Einmaliger Klartext-Key-Anzeige */}
      {newKeyResult && (
        <div className="mb-6 p-4 bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] rounded-lg">
          <p className="text-[var(--tone-warning-fg)] text-sm font-semibold mb-1">
            {t("apiAccess.warningOnce")}
          </p>
          <div className="flex items-center gap-3 mt-3">
            <code className="flex-1 font-mono text-xs bg-[rgb(var(--surface-base-rgb)/0.7)] text-[var(--tone-success-fg)] px-3 py-2 rounded break-all select-all">
              {newKeyResult.plaintextKey}
            </code>
            <button
              onClick={handleCopy}
              title={copied ? t("apiAccess.copied") : t("apiAccess.copy")}
              className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                copied
                  ? "bg-green-700 text-white"
                  : "bg-surface-hover hover:bg-gray-500 text-primary"
              }`}
            >
              {copied ? (
                <>
                  <IconCheck />
                  {t("apiAccess.copied")}
                </>
              ) : (
                <>
                  <IconCopy />
                  {t("apiAccess.copy")}
                </>
              )}
            </button>
          </div>
          {API_V2_ENABLED && newKeyResult.scopes.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {newKeyResult.scopes.map((scope) => (
                <span
                  key={scope}
                  className="px-2 py-0.5 text-xs rounded-full bg-[var(--tone-accent-bg)] text-accent-ink border border-[var(--tone-accent-border)]"
                >
                  {t(`apiAccess.scope.${SCOPE_LABEL_KEYS[scope] ?? scope}`)}
                </span>
              ))}
            </div>
          )}
          <p className="mt-2 text-[var(--tone-warning-fg)] text-xs">
            {t("apiAccess.docsLink")}
          </p>
        </div>
      )}

      {/* Key-Liste */}
      <div>
        {isLoadingKeys ? (
          <div className="flex items-center gap-2 text-muted text-sm py-4">
            <IconRefresh className="animate-spin" />
            <span>{t("apiAccess.loading")}</span>
          </div>
        ) : loadError ? (
          <p className="text-[var(--tone-danger-fg)] text-sm py-4">{loadError}</p>
        ) : keys.length === 0 ? (
          <p className="text-faint text-sm py-4 text-center">
            {t("apiAccess.noKeys")}
          </p>
        ) : (
          <div className="space-y-3">
            {keys.map((key) => (
              <div
                key={key.keyId}
                className={`p-4 rounded-lg border transition-colors ${
                  key.revoked
                    ? "border-hairline bg-[rgb(var(--surface-base-rgb)/0.4)] opacity-60"
                    : "border-strong bg-[rgb(var(--surface-inset-rgb)/0.3)]"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {/* Maskierter Prefix */}
                    <code className="font-mono text-xs text-accent-ink break-all">
                      {key.maskedPrefix}
                    </code>
                    {/* Label */}
                    {key.label && (
                      <p className="text-primary text-sm font-medium mt-1">
                        {key.label}
                      </p>
                    )}
                    {/* Metadaten */}
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
                      <span>
                        {t("apiAccess.created")}:{" "}
                        {formatDate(key.createdAt)}
                      </span>
                      <span>
                        {t("apiAccess.lastUsed")}:{" "}
                        {key.lastUsedAt
                          ? formatDate(key.lastUsedAt)
                          : t("apiAccess.neverUsed")}
                      </span>
                    </div>
                    {/* Scope-Badges (nur bei REACT_APP_API_V2_ENABLED=true) */}
                    {API_V2_ENABLED && key.scopes.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {key.scopes.map((scope) => (
                          <span
                            key={scope}
                            className="px-2 py-0.5 text-xs rounded-full bg-[var(--tone-accent-bg)] text-accent-ink border border-[var(--tone-accent-border)]"
                          >
                            {t(`apiAccess.scope.${SCOPE_LABEL_KEYS[scope] ?? scope}`)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Status + Rotate-/Revoke-Button */}
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    {key.revoked ? (
                      <span className="px-2 py-1 text-xs rounded-full bg-[var(--tone-danger-bg)] text-[var(--tone-danger-fg)] border border-[var(--tone-danger-border)]">
                        {t("apiAccess.revoked")}
                      </span>
                    ) : (
                      <>
                        {API_V2_ENABLED && (
                          <button
                            onClick={() => setRotateTarget(key)}
                            disabled={isRotating}
                            title={t("apiAccess.rotate")}
                            className="flex items-center gap-1 px-3 py-1.5 bg-[var(--tone-accent-bg)] hover:bg-[var(--tone-accent-bg)] hover:brightness-110 text-accent-ink text-xs rounded-md border border-[var(--tone-accent-border)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <IconRefresh />
                            {t("apiAccess.rotate")}
                          </button>
                        )}
                        <button
                          onClick={() => setRevokeTarget(key)}
                          disabled={isRevoking}
                          title={t("apiAccess.revoke")}
                          className="flex items-center gap-1 px-3 py-1.5 bg-[var(--tone-danger-bg)] hover:bg-[var(--tone-danger-bg)] hover:brightness-110 text-[var(--tone-danger-fg)] text-xs rounded-md border border-[var(--tone-danger-border)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <IconDelete />
                          {t("apiAccess.revoke")}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {revokeError && (
          <p className="mt-3 text-sm text-[var(--tone-danger-fg)]">{revokeError}</p>
        )}
        {API_V2_ENABLED && rotateError && (
          <p className="mt-3 text-sm text-[var(--tone-danger-fg)]">{rotateError}</p>
        )}
      </div>

      {/* M12d-4b: Webhook-Endpoint-Verwaltung — nur bei REACT_APP_API_V2_ENABLED=true */}
      {API_V2_ENABLED && <WebhookEndpointsPanel />}

      {/* Widerruf-Bestätigungsdialog */}
      <ConfirmationDialog
        isOpen={revokeTarget !== null}
        title={t("apiAccess.revokeConfirmTitle")}
        message={t("apiAccess.revokeConfirmMessage", {
          key: revokeTarget?.label ?? revokeTarget?.maskedPrefix ?? "",
        })}
        options={[
          {
            label: t("apiAccess.revoke"),
            variant: "danger",
            action: handleRevokeConfirmed,
          },
          {
            label: t("apiAccess.cancel"),
            variant: "secondary",
            action: () => setRevokeTarget(null),
          },
        ]}
        onClose={() => setRevokeTarget(null)}
      />

      {/* Rotations-Bestätigungsdialog (nur bei REACT_APP_API_V2_ENABLED=true erreichbar) */}
      {API_V2_ENABLED && (
        <ConfirmationDialog
          isOpen={rotateTarget !== null}
          title={t("apiAccess.rotateConfirmTitle")}
          message={`${t("apiAccess.rotateConfirmMessage", {
            key: rotateTarget?.label ?? rotateTarget?.maskedPrefix ?? "",
          })} ${t("apiAccess.rotateGraceNote")}`}
          options={[
            {
              label: isRotating ? t("apiAccess.rotating") : t("apiAccess.rotate"),
              variant: "primary",
              action: () => {
                if (!isRotating) {
                  handleRotateConfirmed();
                }
              },
            },
            {
              label: t("apiAccess.cancel"),
              variant: "secondary",
              action: () => setRotateTarget(null),
            },
          ]}
          onClose={() => {
            if (!isRotating) {
              setRotateTarget(null);
            }
          }}
        />
      )}
    </div>
  );
};

export default ApiAccessSection;
